'use client'
import { ReactNode, useMemo, useState } from 'react'
import { IconCubeSend, IconTruckReturn } from '@tabler/icons-react'
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query'

import { useCustomerData } from 'modules/customer'
import { QueryKey } from 'shared/api'
import Button from 'shared/ui/Button'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import Tabs, { TabItem } from 'shared/ui/Tabs/Tabs'

import { fetchSaleOrderHistory } from '../api'
import ProfileSaleOrdersTable from '../components/ProfileSaleOrdersTable'
import { SaleOrder, SaleOrderResponse, SaleOrderReturnStatus } from '../types'

import styles from './ProfileSaleOrders.module.scss'

enum OrdersTab {
  orders = 'orders',
  returns = 'returns'
}

const ordersTabs: TabItem<OrdersTab>[] = [
  {
    id: 'profile-orders-tab-orders',
    label: 'Zamówienia',
    value: OrdersTab.orders,
    panelId: 'profile-orders-panel-orders',
    icon: <IconCubeSend strokeWidth={1.5} />
  },
  {
    id: 'profile-orders-tab-returns',
    label: 'Zwroty',
    value: OrdersTab.returns,
    panelId: 'profile-orders-panel-returns',
    icon: <IconTruckReturn size={20} />
  }
]

interface ProfileSaleOrdersProps {
  token?: string
  limit: number
  campaignBanner?: ReactNode
}

const ProfileSaleOrders = ({
  token,
  limit,
  campaignBanner
}: ProfileSaleOrdersProps) => {
  const [activeTab, setActiveTab] = useState<OrdersTab>(OrdersTab.orders)
  const { user } = useCustomerData()

  const infiniteQuery = useInfiniteQuery<
    SaleOrderResponse,
    Error,
    InfiniteData<SaleOrderResponse, number>,
    readonly unknown[],
    number
  >({
    queryKey: [QueryKey.profileOrders, token, limit],
    queryFn: async ({ pageParam }) => {
      return await fetchSaleOrderHistory({
        token,
        page: pageParam,
        limit
      })
    },
    initialPageParam: 1,

    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.data?.data?.length < limit) {
        return undefined
      }

      return allPages.length + 1
    }
  })

  const orders = useMemo((): SaleOrder[] => {
    return infiniteQuery.data?.pages.flatMap(page => page.data.data) ?? []
  }, [infiniteQuery.data])

  const returnOrders = useMemo(
    () =>
      orders.filter(
        order =>
          !!order.returnStatus &&
          order.returnStatus !== SaleOrderReturnStatus.UNAVAILABLE &&
          order.returnStatus !== SaleOrderReturnStatus.AVAILABLE
      ),
    [orders]
  )

  const hasReturnOrders = returnOrders.length > 0

  const meta = infiniteQuery.data?.pages?.[0]?.data?.meta

  const visibleOrders = useMemo(
    () =>
      hasReturnOrders && activeTab === OrdersTab.returns
        ? returnOrders
        : orders,
    [activeTab, hasReturnOrders, orders, returnOrders]
  )

  const displayedOrdersCount = useMemo(() => {
    if (hasReturnOrders && activeTab === OrdersTab.returns) {
      return returnOrders.length
    }

    return meta?.total
  }, [activeTab, hasReturnOrders, meta?.total, returnOrders.length])

  const activeTabDefinition = hasReturnOrders
    ? ordersTabs.find(tab => tab.value === activeTab)
    : undefined

  const shouldShowLoadMoreInEmptyState =
    activeTab === OrdersTab.returns &&
    visibleOrders.length === 0 &&
    infiniteQuery.hasNextPage

  return (
    <div>
      <div className={styles.title} id='ordersHeadline'>
        <span id='profile_details_my_orders'>Zamówienia</span>
        {displayedOrdersCount !== undefined && displayedOrdersCount > 0 && (
          <div className={styles.ordersNumber}>
            ({displayedOrdersCount || ''})
          </div>
        )}
      </div>
      {campaignBanner}
      {hasReturnOrders && (
        <div className={styles.tabsWrapper}>
          <Tabs<OrdersTab>
            tabs={ordersTabs}
            selected={activeTab}
            onTabChange={setActiveTab}
            ariaLabel='Rodzaje historii zamówień'
          />
        </div>
      )}
      {infiniteQuery.isLoading ? (
        <div className={styles.emptyStateWrapper}>
          <LoadingIndicator size={40} />
        </div>
      ) : visibleOrders.length > 0 ? (
        <div
          id={activeTabDefinition?.panelId || ordersTabs[0].panelId}
          role='tabpanel'
          aria-labelledby={activeTabDefinition?.id || ordersTabs[0].id}
        >
          {user && <ProfileSaleOrdersTable user={user} orders={visibleOrders} />}
          {infiniteQuery.hasNextPage && (
            <Button
              onClick={() => infiniteQuery.fetchNextPage()}
              size='small'
              variant='primaryOutline'
              loading={infiniteQuery.isFetchingNextPage}
            >
              Pokaż więcej
            </Button>
          )}
        </div>
      ) : (
        <div className={styles.emptyStateWrapper}>
          <p>
            {activeTab === OrdersTab.returns
              ? 'Brak zwrotów do wyświetlenia'
              : 'Brak zamówień do wyświetlenia'}
          </p>
          {shouldShowLoadMoreInEmptyState && (
            <Button
              onClick={() => infiniteQuery.fetchNextPage()}
              size='small'
              variant='primaryOutline'
              loading={infiniteQuery.isFetchingNextPage}
            >
              Pokaż więcej
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default ProfileSaleOrders
